import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const required = z.string().trim().min(1);

const attributeValueDtoSchema = z.union([z.string(), z.number(), z.boolean()]);

export const assetStatusDtoSchema = z.enum(["ACTIVE", "INACTIVE"]);

export const assetAttachmentDtoSchema = z.object({
  id: required,
  fileUrl: required,
  extension: required,
  createdAt: required,
});

export const assetCreateDtoSchema = z.object({
  accountId: required.optional(),
  assetTypeId: required,
  branchId: required,
  nameEn: required,
  nameAr: required,
  notes: z.string().trim().optional(),
  attributes: z.record(z.string(), attributeValueDtoSchema),
});

export const assetDtoSchema = z.object({
  id: required,
  code: required,
  nameEn: required,
  nameAr: required,
  notes: z.string().nullable(),
  // Optional -- not confirmed present on the create response; PATCH's own
  // response does carry it. Defaulted to "ACTIVE" in assetFromDto when absent.
  status: assetStatusDtoSchema.optional(),
  // The create/update response's own raw value bag -- distinct from
  // AssetDetail's resolved `attributes[]` array (see assetDetailDtoSchema).
  attributes: z.record(z.string(), z.unknown()),
  // Present when files were uploaded this request -- defaulted to `[]` in
  // assetFromDto otherwise, same convention as companyDtoSchema's own.
  attachments: z.array(assetAttachmentDtoSchema).optional(),
  createdAt: required,
  updatedAt: required,
  accountId: required,
  assetTypeId: required,
  // Same tolerant-null convention as assetTypeDtoSchema.
  createdById: required.nullable().optional(),
  updatedById: required.nullable().optional(),
});

export const assetResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: assetDtoSchema,
});

export const assetFormFieldSourceDtoSchema = z.enum(["FIXED", "ATTRIBUTE"]);
export const assetFormFieldTypeDtoSchema = z.enum([
  "STRING",
  "NUMBER",
  "BOOLEAN",
  "DATE",
  // The accountId fixed field (staff only) -- ignored client-side (see the
  // "accountId"/"branchId" filter in AssetFormScreen), but still a real
  // value on the wire, so it has to parse.
  "UUID",
  // The branchId fixed field -- also filtered out client-side, already
  // covered by its own dedicated (searchable, GET /branches-backed) combobox.
  "DROPDOWN",
]);

export const assetFormFieldValidationDtoSchema = z.object({
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().nonnegative().optional(),
  pattern: z.string().optional(),
  patternFlags: z.string().optional(),
  // Seen on UUID (format: "uuid") and DATE (format: "date-time") fields.
  // Not read client-side yet -- DATE only client-checks isRequired (see
  // AssetFormScreen.validateFields), and UUID fields aren't rendered at
  // all -- kept for fidelity with the real response.
  format: z.string().optional(),
});

export const assetFormFieldDtoSchema = z.object({
  key: required,
  source: assetFormFieldSourceDtoSchema,
  fieldType: assetFormFieldTypeDtoSchema,
  nameEn: required,
  nameAr: required,
  isRequired: z.boolean(),
  isUnique: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  validation: assetFormFieldValidationDtoSchema.optional(),
});

export const assetCreateFormAssetTypeDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  attachmentsAllowed: z.boolean(),
  status: assetStatusDtoSchema,
});

export const assetCreateFormDtoSchema = z.object({
  assetType: assetCreateFormAssetTypeDtoSchema,
  fields: z.array(assetFormFieldDtoSchema),
});

export const assetCreateFormResponseDtoSchema = z.object({
  data: assetCreateFormDtoSchema,
});

// `PATCH /assets/:id` -- status, nameEn/nameAr, notes, and attributes are
// editable; a null attribute value clears an optional field (refused
// server-side on a required one).
export const assetUpdateDtoSchema = z.object({
  status: assetStatusDtoSchema.optional(),
  nameEn: required.optional(),
  nameAr: required.optional(),
  notes: z.string().trim().optional(),
  branchId: required.optional(),
  attributes: z
    .record(z.string(), attributeValueDtoSchema.nullable())
    .optional(),
});

const assetDetailAttributeValueDtoSchema = attributeValueDtoSchema.nullable();

export const assetDetailAttributeDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  type: assetFormFieldTypeDtoSchema,
  isRequired: z.boolean(),
  isUnique: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  value: assetDetailAttributeValueDtoSchema,
});

export const assetDetailAccountDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
});

export const assetDetailAssetTypeDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  status: assetStatusDtoSchema,
  // Assumed present, mirroring assetCreateFormAssetTypeDtoSchema -- not
  // confirmed by a pasted swagger for this endpoint. Defaulted to `false`
  // in assetDetailFromDto if the field is ever actually absent.
  attachmentsAllowed: z.boolean().optional(),
});

// `GET /assets/:id` -- the read-only details screen shape, distinct from
// assetDtoSchema (the create/update response): account/assetType come
// embedded, and attributes is an array of resolved fields, not a raw bag.
export const assetDetailDtoSchema = z.object({
  id: required,
  code: required,
  nameEn: required,
  nameAr: required,
  notes: z.string().nullable(),
  status: assetStatusDtoSchema,
  createdAt: required,
  updatedAt: required,
  account: assetDetailAccountDtoSchema,
  // Same embedded {id, nameEn, nameAr} shape as account -- now returned here
  // too, mirroring assetListItemDtoSchema's own "branch".
  branch: assetDetailAccountDtoSchema,
  assetType: assetDetailAssetTypeDtoSchema,
  attributes: z.array(assetDetailAttributeDtoSchema),
  // Assumed present -- see the note on assetDetailAssetTypeDtoSchema and
  // AssetUpdateInput.
  attachments: z.array(assetAttachmentDtoSchema).optional(),
});

export const assetDetailResponseDtoSchema = z.object({
  data: assetDetailDtoSchema,
});

// `GET /assets` -- lighter than assetDetailDtoSchema: no notes or
// attributes, just enough to list and link into the detail screen. Reuses
// the same embedded account/assetType shape.
export const assetListItemDtoSchema = z.object({
  id: required,
  code: required,
  nameEn: required,
  nameAr: required,
  status: assetStatusDtoSchema,
  createdAt: required,
  updatedAt: required,
  account: assetDetailAccountDtoSchema,
  assetType: assetDetailAssetTypeDtoSchema,
  // Same embedded {id, nameEn, nameAr} shape as account.
  branch: assetDetailAccountDtoSchema,
});

export const assetsListResponseDtoSchema = z.object({
  data: z.array(assetListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

// `DELETE /assets/:id` -- a bare `{ message }` envelope, no record.
export const assetDeleteResponseDtoSchema = z.object({
  message: required,
});

// Confirmed: `POST /assets` returns 201 for both a real creation (the full
// assetDtoSchema above) and an archived-name match, which creates nothing
// and returns the archived asset's summary with `isArchived: true` instead.
// The summary's exact field set beyond `id` isn't pinned down, so kept
// defensive (only `id` required) same as the asset-types precedent -- a real
// creation is tried first via assetResponseDtoSchema.safeParse and only
// falls through to this trimmed shape when that fails.
export const assetArchivedMatchDtoSchema = z.object({
  id: required,
  nameEn: required.optional(),
  nameAr: required.optional(),
  isArchived: z.literal(true).optional(),
});

export const assetArchivedMatchResponseDtoSchema = z.object({
  data: assetArchivedMatchDtoSchema,
  message: required,
});

const archivedAssetTypeRefDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  status: assetStatusDtoSchema,
});

// `GET /assets/archived` -- not a full asset record, just enough to list and
// restore. Reuses the same embedded account shape as the detail/list DTOs;
// assetType here has no attachmentsAllowed in the confirmed example, so it
// gets its own trimmed ref schema rather than assetDetailAssetTypeDtoSchema.
export const archivedAssetListItemDtoSchema = z.object({
  id: required,
  code: required,
  nameEn: required,
  nameAr: required,
  status: assetStatusDtoSchema,
  account: assetDetailAccountDtoSchema,
  assetType: archivedAssetTypeRefDtoSchema,
  archivedAt: required,
});

export const archivedAssetsListResponseDtoSchema = z.object({
  data: z.array(archivedAssetListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export type AssetAttachmentDto = z.infer<typeof assetAttachmentDtoSchema>;
export type AssetCreateDto = z.infer<typeof assetCreateDtoSchema>;
export type AssetDto = z.infer<typeof assetDtoSchema>;
export type AssetFormFieldDto = z.infer<typeof assetFormFieldDtoSchema>;
export type AssetCreateFormDto = z.infer<typeof assetCreateFormDtoSchema>;
export type AssetUpdateDto = z.infer<typeof assetUpdateDtoSchema>;
export type AssetDetailAttributeDto = z.infer<
  typeof assetDetailAttributeDtoSchema
>;
export type AssetDetailDto = z.infer<typeof assetDetailDtoSchema>;
export type AssetListItemDto = z.infer<typeof assetListItemDtoSchema>;
export type AssetArchivedMatchDto = z.infer<typeof assetArchivedMatchDtoSchema>;
export type ArchivedAssetListItemDto = z.infer<
  typeof archivedAssetListItemDtoSchema
>;
